import './RequestForms.css';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';
import api from '../../api/axios';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useNavigate } from 'react-router-dom';
import { HelpTooltip } from '../../components/ui/HelpTooltip';
import { buildRequestSubmissionState } from '../../utils/requestSubmission';
import ProjectSelector from '../../components/projects/ProjectSelector';
import useWarehouses from '../../hooks/useWarehouses';
import useWarehouseStockItems from '../../hooks/useWarehouseStockItems';
import UrgentRequestToggle from '../../components/requests/UrgentRequestToggle';

const WarehouseSupplyRequestForm = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([{ stock_item_id: '', item_name: '', quantity: 1 }]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [supplyWarehouseId, setSupplyWarehouseId] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [itemSearchTerms, setItemSearchTerms] = useState(['']);

  const navigate = useNavigate();
  const { user, loading, error } = useCurrentUser();
  const { warehouses, loading: warehousesLoading, error: warehousesError } = useWarehouses();
  const {
    items: warehouseItems,
    loading: warehouseItemsLoading,
    error: warehouseItemsError,
  } = useWarehouseStockItems(supplyWarehouseId);
  const targetDeptId = user?.department_id;
  const targetSectionId = user?.section_id;

  // No stock lookup - items are manually entered by the user

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get('/warehouse-supply-templates');
        setTemplates(res.data || []);
      } catch (err) {
        if (err?.response?.status !== 403) {
          console.error('Failed to load templates:', err);
        }
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!supplyWarehouseId && warehouses.length > 0) {
      const preferred = user?.warehouse_id || warehouses[0]?.id;
      if (preferred) {
        setSupplyWarehouseId(String(preferred));
      }
    }
  }, [supplyWarehouseId, warehouses, user]);

  const applyTemplate = (id) => {
    setSelectedTemplateId(id);
    const template = templates.find((t) => t.id === parseInt(id, 10));
    if (template) {
      if (!supplyWarehouseId) {
        alert(t('warehouseSupplyRequestForm.alerts.selectWarehouseFirst'));
        return;
      }

      const mapped = (template.items || [])
        .map((it) => {
          const matchedItem = warehouseItems.find(
            (whItem) => whItem.item_name?.toLowerCase() === (it.item_name || '').toLowerCase(),
          );
          if (!matchedItem) return null;
          return {
            stock_item_id: matchedItem.stock_item_id?.toString() || '',
            item_name: matchedItem.item_name || '',
            quantity: 1,
          };
        })
        .filter(Boolean);

      if (mapped.length === 0) {
        alert(t('warehouseSupplyRequestForm.alerts.noTemplateItems'));
      }

      setItems(mapped.length ? mapped : [{ stock_item_id: '', item_name: '', quantity: 1 }]);
    }
  };

  const addItem = () =>
    {
      setItems([...items, { stock_item_id: '', item_name: '', quantity: 1 }]);
      setItemSearchTerms((prev) => [...prev, '']);
    };
  const removeItem = (idx) => {
    if (!window.confirm(t('warehouseSupplyRequestForm.alerts.removeItem'))) return;
    setItems(items.filter((_, i) => i !== idx));
    setItemSearchTerms((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleWarehouseChange = (value) => {
    setSupplyWarehouseId(value);
    setSelectedTemplateId('');
    setItems([{ stock_item_id: '', item_name: '', quantity: 1 }]);
    setItemSearchTerms(['']);
    setCategory('');
    setSubCategory('');
  };

  const formatExpiry = (value) => {
    if (!value) return t('warehouseSupplyRequestForm.alerts.noExpiry');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const handleItemSelection = (index, stockItemId) => {
    const selected = warehouseItems.find(
      (it) => String(it.stock_item_id) === String(stockItemId),
    );
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      stock_item_id: stockItemId,
      item_name: selected?.item_name || '',
    };
    setItems(updated);
  };

  const categories = useMemo(
    () => Array.from(new Set(warehouseItems.map((item) => item.category).filter(Boolean))).sort(),
    [warehouseItems],
  );

  const subCategories = useMemo(() => {
    const scoped = category
      ? warehouseItems.filter((item) => item.category === category)
      : warehouseItems;
    return Array.from(new Set(scoped.map((item) => item.sub_category).filter(Boolean))).sort();
  }, [warehouseItems, category]);

  const filteredCatalog = useMemo(
    () => warehouseItems.filter((item) => {
      if (category && item.category !== category) return false;
      if (subCategory && item.sub_category !== subCategory) return false;
      return true;
    }),
    [warehouseItems, category, subCategory],
  );

  const handleQuantityChange = (index, value) => {
    const updated = [...items];
    updated[index].quantity = parseInt(value, 10);
    setItems(updated);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      alert(t('warehouseSupplyRequestForm.alerts.justificationRequired'));
      return;
    }
    if (!targetDeptId) {
      alert(t('warehouseSupplyRequestForm.alerts.missingDepartment'));
      return;
    }
    if (!supplyWarehouseId) {
      alert(t('warehouseSupplyRequestForm.alerts.selectFulfillmentWarehouse'));
      return;
    }
    const hasInvalid = items.some(
      (i) => !i.stock_item_id || !i.item_name.trim() || i.quantity < 1,
    );
    if (hasInvalid) {
      alert(t('warehouseSupplyRequestForm.alerts.invalidItems'));
      return;
    }

    const payload = new FormData();
    payload.append('request_type', 'Warehouse Supply');
    payload.append('justification', justification);
    payload.append('target_department_id', targetDeptId);
    payload.append('target_section_id', targetSectionId || '');
    payload.append('budget_impact_month', '');
    payload.append('supply_warehouse_id', supplyWarehouseId);
    payload.append('project_id', projectId || '');
    const mapped = items.map(it => ({ item_name: it.item_name, quantity: it.quantity }));
    payload.append('items', JSON.stringify(mapped));
    payload.append('is_urgent', isUrgent ? 'true' : 'false');

    try {
      setSubmitting(true);
      const res = await api.post('/requests', payload);
      const state = buildRequestSubmissionState('Warehouse Supply', res.data);
      navigate('/request-submitted', { state });
    } catch (err) {
      console.error('Submission failed:', err);
      alert(err.response?.data?.message || t('warehouseSupplyRequestForm.alerts.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
          <div className="p-6 text-center text-gray-600">{t('warehouseSupplyRequestForm.loading')}</div>
      </>
    );
  }

  if (error || !user) {
    return (
      <>
          <div className="p-6 text-center text-red-600">{t('warehouseSupplyRequestForm.loadUserFailed')}</div>
      </>
    );
  }

  return (
    <>
      <div className="request-form-page request-form-warehouse">
        <h1 className="text-2xl font-bold mb-4">
          {t('warehouseSupplyRequestForm.title')}
          <HelpTooltip text={t('warehouseSupplyRequestForm.title')} />
        </h1>
        <p className="request-form-intro">{t('requestFormLayout.warehouseIntro')}</p>

        <form onSubmit={handleSubmit} className="request-form-content">
          <section className="request-form-panel request-details"><h2>{t('requestFormLayout.requestDetails')}</h2>
          <div>
            <label htmlFor="WarehouseSupplyRequestForm-justification" className="block font-semibold mb-1">{t('warehouseSupplyRequestForm.fields.justification')}</label>
            <textarea id="WarehouseSupplyRequestForm-justification"
              className="w-full p-2 border rounded"
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="WarehouseSupplyRequestForm-fulfillmentWarehouse" className="block font-semibold mb-1">{t('warehouseSupplyRequestForm.fields.fulfillmentWarehouse')}</label>
            <select id="WarehouseSupplyRequestForm-fulfillmentWarehouse"
              value={supplyWarehouseId}
              onChange={(e) => handleWarehouseChange(e.target.value)}
              className="p-2 border rounded"
              disabled={submitting || warehousesLoading || warehouses.length === 0}
              required
            >
              <option value="">{t('warehouseSupplyRequestForm.fields.selectWarehouse')}</option>
              {warehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>
                  {wh.name}
                </option>
              ))}
            </select>
            {warehousesError && (
              <p className="text-sm text-red-600 mt-1">{warehousesError}</p>
            )}
          </div>

          <div>
            <label htmlFor="WarehouseSupplyRequestForm-template" className="block font-semibold mb-1">{t('warehouseSupplyRequestForm.fields.template')}</label>
            <select id="WarehouseSupplyRequestForm-template"
              value={selectedTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="p-2 border rounded"
              disabled={submitting}
            >
              <option value="">{t('warehouseSupplyRequestForm.fields.selectTemplate')}</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.template_name}
                </option>
              ))}
            </select>
          </div>

          <ProjectSelector
            value={projectId}
            onChange={setProjectId}
            disabled={submitting}
            user={user}
          />

          </section>
          <div className="request-form-panel request-items">
            <label className="block font-semibold mb-2">{t('warehouseSupplyRequestForm.fields.items')}</label>
            <div className="grid gap-4 md:grid-cols-2 mb-4">
              <div>
                <label htmlFor="WarehouseSupplyRequestForm-category" className="block font-semibold mb-1">{t('warehouseSupplyRequestForm.fields.category')}</label>
                <select id="WarehouseSupplyRequestForm-category"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSubCategory('');
                  }}
                  className="w-full p-2 border rounded"
                  disabled={submitting || warehouseItemsLoading}
                >
                  <option value="">{t('warehouseSupplyRequestForm.fields.allCategories')}</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="WarehouseSupplyRequestForm-subCategory" className="block font-semibold mb-1">{t('warehouseSupplyRequestForm.fields.subCategory')}</label>
                <select id="WarehouseSupplyRequestForm-subCategory"
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                  className="w-full p-2 border rounded"
                  disabled={submitting || warehouseItemsLoading}
                >
                  <option value="">{t('warehouseSupplyRequestForm.fields.allSubCategories')}</option>
                  {subCategories.map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
            </div>
            {supplyWarehouseId === '' && (
              <p className="text-sm text-gray-600 mb-2">
                {t('warehouseSupplyRequestForm.fields.selectWarehouseHelp')}
              </p>
            )}
            {warehouseItemsError && (
              <p className="text-sm text-red-600 mb-2">{warehouseItemsError}</p>
            )}
            {warehouseItemsLoading && (
              <p className="text-sm text-gray-600 mb-2">{t('warehouseSupplyRequestForm.fields.loadingItems')}</p>
            )}
            {supplyWarehouseId && !warehouseItemsLoading && warehouseItems.length === 0 && (
              <p className="text-sm text-gray-600 mb-2">
                {t('warehouseSupplyRequestForm.fields.emptyWarehouse')}
              </p>
            )}
            {items.map((it, idx) => (
              <div key={idx} className="request-item-card warehouse-item-row">
                <input
                  type="search"
                  value={itemSearchTerms[idx] || ''}
                  onChange={(e) =>
                    setItemSearchTerms((prev) => {
                      const next = [...prev];
                      next[idx] = e.target.value;
                      return next;
                    })
                  }
                  placeholder={t('warehouseSupplyRequestForm.fields.itemSearchPlaceholder')}
                  className="flex-1 p-2 border rounded min-w-[200px]"
                  disabled={submitting || warehouseItemsLoading}
                />
                <select
                  value={it.stock_item_id}
                  onChange={(e) => handleItemSelection(idx, e.target.value)}
                  className="flex-1 p-2 border rounded min-w-[200px]"
                  required
                  disabled={
                    submitting ||
                    warehouseItemsLoading ||
                    !supplyWarehouseId ||
                    warehouseItems.length === 0
                  }
                >
                  <option value="">{t('warehouseSupplyRequestForm.fields.selectItem')}</option>
                  {filteredCatalog
                    .filter((item) => {
                      const q = (itemSearchTerms[idx] || '').trim().toLowerCase();
                      if (!q) return true;
                      return (
                        String(item.stock_item_id || '').toLowerCase().includes(q) ||
                        String(item.item_name || '').toLowerCase().includes(q)
                      );
                    })
                    .map((item) => (
                    <option key={item.stock_item_id} value={item.stock_item_id}>
                      {item.item_name} ({t('warehouseSupplyRequestForm.fields.expiry')}: {formatExpiry(item.expiry_date)})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  value={it.quantity}
                  onChange={(e) => handleQuantityChange(idx, e.target.value)}
                  className="w-24 p-2 border rounded"
                  required
                  disabled={submitting}
                />
                {items.length > 1 && (
                  <button type="button" onClick={() => removeItem(idx)} aria-label={t('requestFormLayout.removeItem', { index: idx + 1 })} className="text-red-600 text-lg" disabled={submitting}>✕</button>
                )}
              </div>
            ))}
            <button type="button" onClick={addItem} className="request-add-item" disabled={submitting}>{t('warehouseSupplyRequestForm.fields.addItem')}</button>
          </div>

          <UrgentRequestToggle
            user={user}
            checked={isUrgent}
            onChange={setIsUrgent}
            disabled={submitting}
          />

          <button
            type="submit"
            disabled={submitting}
            className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {submitting ? t('warehouseSupplyRequestForm.fields.submitting') : t('warehouseSupplyRequestForm.fields.submit')}
          </button>
        </form>
      </div>
    </>
  );
};

export default WarehouseSupplyRequestForm;